import React, {useEffect} from "react";
import {Helmet} from "react-helmet";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {
    getIngredients,
    setAdditionDialogActive,
    setAdditionRemoved,
    setAdditionToRemove,
    setErrorData,
    setFilterExpanded,
    setFilteringActive,
    setFilterValue,
    setIngredients
} from "../../../../slices/additionsSlice";

import {AdditionFormDialog} from "./AdditionFormDialog";
import {remove} from "../../../../slices/objectRemovalSlice";
import {DecisionDialog} from "../dialog-windows/DecisionDialog";
import {filter} from "../../../../slices/filteringSlice";
import {SearchButton} from "../dishes-categories/SearchButton";
import {LetterGroup} from "./LetterGroup";
import {useConfirmationMessage} from "../../../../hooks/useConfirmationMessage";
import {fallbackLng} from "../../../../apiData";
import {BorderedButton} from "../../common/BorderedButton";
import {PlusIcon} from "../../../icons/PlusIcon";
import {AdditionsSkeleton} from "./AdditionsSkeleton";
import {InformationTooltip} from "../shared-components/InformationTooltip";

export const Additions = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {restaurant} = useSelector(state => state.dashboard.view);
    const restaurantLanguage = restaurant?.value?.settings?.language?.toLowerCase() ?? fallbackLng;
    const {
        filterValue,
        filterExpanded,
        additionDialogActive,
        additionToRemove,
        filteringActive
    } = useSelector(state => state.additions.view);
    const {ingredients} = useSelector(state => state.additions.getIngredients);
    const removalPending = useSelector(state => state.objRemoval.isLoading);
    const confirmAdditionRemoval = useConfirmationMessage(setAdditionRemoved);
    const letters = [...new Set(ingredients?.map((ingredient) =>
        ingredient.name[restaurantLanguage]?.toUpperCase()?.[0]
    ))];

    useEffect(() => {
        if (!ingredients) dispatch(getIngredients());
    }, [dispatch, ingredients]);

    useEffect(() => {
        if (!filterExpanded && filterValue !== '') {
            dispatch(setFilterValue(''));
            executeFilter('');
        }
    }, [dispatch, filterExpanded]);

    const filterIngredientsByLetter = (letter) => {
        return ingredients?.filter((ingredient) =>
            ingredient.name[restaurantLanguage]?.toUpperCase()?.[0] === letter);
    }

    const handleAdditionRemoval = async (e, ingredient) => {
        e.preventDefault();
        const resultAction = await dispatch(remove({id: ingredient.id, path: 'ingredients'}));
        if (remove.fulfilled.match(resultAction)) {
            dispatch(setAdditionToRemove(null));
            filteringActive ? await executeFilter(filterValue) : await dispatch(getIngredients());
            confirmAdditionRemoval();
        } else if (remove.rejected.match(resultAction)) {
            setErrorData(resultAction.payload);
        }
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault()
        dispatch(setFilterValue(e.target.value));
        await executeFilter(e.target.value);
    }

    const executeFilter = async value => {
        if ('' !== value) {
            dispatch(setFilteringActive(true));
            const resultAction = await dispatch(filter({path: 'cms/ingredients', value: value}));
            if (filter.fulfilled.match(resultAction)) {
                dispatch(setIngredients(resultAction.payload));
            }
        } else {
            dispatch(setFilteringActive(false));
            dispatch(getIngredients());
        }
    }

    if (!ingredients) return <AdditionsSkeleton/>;

    return (
        <>
            <Helmet>
                <title>CMS - {t("additions")}</title>
            </Helmet>
            {additionDialogActive && <AdditionFormDialog filter={executeFilter}/>}
            {additionToRemove &&
                <DecisionDialog msg={t('confirmAdditionRemoval')}
                                objName={additionToRemove.name}
                                onSubmit={(e) => handleAdditionRemoval(e, additionToRemove)}
                                onCancel={() => dispatch(setAdditionToRemove(null))}
                                isLoading={removalPending}
                                isRemoval={true}/>}
            <div className={'cms-padded-view-container'}>
                <div className={'functions-header'}>
                    <div className={'section-heading'} style={{display: 'flex', alignItems: 'flex-start'}}>
                        <span style={{marginRight: '5px'}}>{t('additions')}</span>
                        <InformationTooltip text={t('additionsViewInfo')} nonAbsolute/>
                    </div>
                    <div className={'flex-wrapper-gapped'}>
                        <BorderedButton icon={<PlusIcon/>}
                                        isPlus={true}
                                        text={t('newAddition')}
                                        onClick={() => dispatch(setAdditionDialogActive(true))}/>
                        <SearchButton filterExpanded={filterExpanded}
                                      onExpand={() => dispatch(setFilterExpanded(!filterExpanded))}
                                      filterValue={filterValue}
                                      onSubmit={handleSearchSubmit}
                                      onClear={() => {
                                          dispatch(setFilterValue(''));
                                          dispatch(getIngredients());
                                      }}
                        />
                    </div>
                </div>
                <div className={'scrollable-wrapper'}>
                    <div className={'scroll-container'}>
                        {ingredients?.length === 0 && <p className={'text-center'}>{t('noAdditions')}</p>}
                        {letters?.map(letter => (
                            <LetterGroup key={letter}
                                         letter={letter}
                                         ingredients={filterIngredientsByLetter(letter)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}